// GALLERY-ONLY MOCK of a FogView, for art review without a server (`?gallery=1&fog=…`).
// This is NOT the rules: who sees what is decided by the sim (`fk_sim::fog`, docs/31) and reaches
// the client as `Snapshot.fog`. The mock only needs to look plausible — a simple ray cast from a
// few heroes, darkness that shortens sight unless a hero has darkvision or a square is lit, torches
// underground, some explored memory behind the party (as if it came in from the west), the foes
// out of sight turned into last-seen marks, and one or two foes only heard.
import type { FogEyes, FogMark, FogVeil, FogView, Tile, TileData } from '../../core/sim/types';
import { cellHash } from '../tiling/hash';

export interface MockEye { unit: number; tile: Tile; darkvision?: boolean; keen?: boolean }
export interface MockFoe { unit: number; tile: Tile; look: number }

const VEIL_TEXT: Record<FogVeil['key'], { name: string; line: string }> = {
  night: { name: 'Night', line: 'Sight 5 squares; darkvision sees 6.' },
  underground: { name: 'Lightless', line: 'Sight 1–2 squares unless lit; heroes carry torches.' },
  weather: { name: 'Thick weather', line: 'Sight 4 squares; keen eyes see 5.' },
  rooms: { name: 'Closed doors', line: 'You see the rooms you can walk into; what lies behind a shut door stays dark until it opens.' },
};

const blocks = (t: TileData | undefined): boolean => !t || t.terrain === 'Wall' || (t.terrain === 'Door' && !t.doorOpen) || t.opaque;

/** Squares in a clear line from `from` within `range` (walls are seen, never seen past). */
export function mockLos(board: { w: number; h: number; tiles: ReadonlyArray<TileData | undefined> }, from: Tile, range: number): Set<number> {
  const out = new Set<number>();
  const { w, h, tiles } = board;
  for (let y = Math.max(0, from.y - range); y <= Math.min(h - 1, from.y + range); y++) for (let x = Math.max(0, from.x - range); x <= Math.min(w - 1, from.x + range); x++) {
    if (Math.hypot(x - from.x, y - from.y) > range + 0.35) continue;
    const steps = Math.max(Math.abs(x - from.x), Math.abs(y - from.y));
    let clear = true;
    for (let s = 1; s < steps && clear; s++) {
      const cx = Math.round(from.x + ((x - from.x) * s) / steps), cy = Math.round(from.y + ((y - from.y) * s) / steps);
      if ((cx !== x || cy !== y) && blocks(tiles[cy * w + cx])) clear = false;
    }
    if (clear) out.add(y * w + x);
  }
  return out;
}

/** The unit step toward the exits from the party's middle (`[-1, 0]`, west, with no exits). */
export function backOf(board: { exits?: ReadonlyArray<Tile> }, eyes: ReadonlyArray<{ tile: Tile }>): [number, number] {
  const ex = board.exits ?? [];
  if (!ex.length || !eyes.length) return [-1, 0];
  const mx = (ts: ReadonlyArray<Tile>, k: 'x' | 'y') => ts.reduce((s, t) => s + t[k], 0) / ts.length;
  const dx = mx(ex, 'x') - mx(eyes.map((e) => e.tile), 'x'), dy = mx(ex, 'y') - mx(eyes.map((e) => e.tile), 'y');
  return Math.abs(dx) >= Math.abs(dy) ? [Math.sign(dx) || -1, 0] : [0, Math.sign(dy) || 1];
}

export function mockFog(board: { w: number; h: number; tiles: ReadonlyArray<TileData | undefined>; exits?: ReadonlyArray<Tile> }, veils: FogVeil['key'][], eyes: MockEye[], foes: MockFoe[], seed = 1): { fog: FogView; seen: Set<number> } {
  const { w, h, tiles } = board;
  const n = w * h;
  const dark = veils.includes('underground');
  const night = veils.includes('night');
  const weather = veils.includes('weather');
  const lit = new Array<0 | 1>(n).fill(0);
  // underground: every hero without darkvision carries a torch (light 3); fire and lava glow
  if (dark) {
    for (const e of eyes) if (!e.darkvision) for (const i of mockLos(board, e.tile, 3)) lit[i] = 1;
    for (let i = 0; i < n; i++) if (tiles[i]?.hazard === 'Fire' || tiles[i]?.hazard === 'Burning') for (const j of mockLos(board, { x: i % w, y: Math.floor(i / w) }, 1)) lit[j] = 1;
  }
  const rangeOf = (e: MockEye): { sight: number; dim: number } => {
    let sight = e.keen ? 8 : 7;
    if (weather) sight = Math.min(sight, e.keen ? 5 : 4);
    if (night && !e.darkvision) sight = Math.min(sight, 5);
    const dim = dark && !e.darkvision ? 2 : sight;
    return { sight: dark && e.darkvision ? Math.min(sight, 6) : sight, dim };
  };
  const sees = (e: MockEye, at: Tile, torch = false): Set<number> => {
    const r = rangeOf(e);
    const sight = r.sight, dim = torch && dark && !e.darkvision ? 3 : r.dim;
    const out = new Set<number>();
    for (const i of mockLos(board, at, sight)) {
      const d = Math.hypot((i % w) - at.x, Math.floor(i / w) - at.y);
      if (d <= dim + 0.35 || lit[i] === 1) out.add(i);
    }
    return out;
  };
  const view: FogEyes[] = eyes.map((e) => {
    const { sight, dim } = rangeOf(e);
    const tiles = sees(e, e.tile);
    return { unit: e.unit, sight, dark: dim, darkvision: !!e.darkvision, keen: !!e.keen, light: dark && !e.darkvision ? 3 : 0, scent: 0, tiles: [...tiles].sort((a, b) => a - b), words: [] };
  });
  const cells = new Array<0 | 1 | 2>(n).fill(0);
  // where the party has been: the same eyes from a few squares round about (back west, and scouting
  // ahead along the walls), so there is explored memory to draw
  // docs/66 RM13: "back" is toward the party's way in, wherever the exits are (west by default)
  const back = backOf(board, eyes);
  const turn = ([a, b]: readonly [number, number]): [number, number] => [a * back[0] - b * back[1], a * back[1] + b * back[0]];
  const OFFSETS = ([[3, 0], [6, 0], [0, -4], [0, 4], [-3, -3], [-3, 3]] as const).map(turn);
  for (const e of eyes) for (const [dx, dy] of OFFSETS) {
    const x = e.tile.x + dx, y = e.tile.y + dy;
    if (x < 0 || y < 0 || x >= w || y >= h || cellHash('fog-back', e.unit * 7 + dx, dy + seed) % 3 === 0) continue;
    const t = tiles[y * w + x];
    if (t && !blocks(t)) for (const i of sees(e, { x, y }, true)) cells[i] = 1;
  }
  for (const v of view) for (const i of v.tiles) cells[i] = 2;
  const pulse = 6;
  const seen = new Set<number>();
  const marks: FogMark[] = [];
  foes.forEach((f, k) => {
    if (cells[f.tile.y * w + f.tile.x] === 2) { seen.add(f.unit); return; }
    marks.push({ unit: f.unit, tile: f.tile, pulse: pulse - 1 - (k % 3), sense: 'seen', look: f.look });
  });
  // a foe only heard, somewhere in the unexplored dark (and smelled, underground)
  const dark0: number[] = [];
  for (let i = 0; i < n; i++) if (cells[i] === 0 && !blocks(tiles[i])) dark0.push(i);
  if (dark0.length) {
    const i = dark0[cellHash('fog-heard', seed, n) % dark0.length]!;
    marks.push({ unit: 900, tile: { x: i % w, y: Math.floor(i / w) }, pulse, sense: 'heard', look: 0 });
    if (dark && dark0.length > 4) {
      const j = dark0[cellHash('fog-smelled', seed, n + 1) % dark0.length]!;
      if (j !== i) marks.push({ unit: 901, tile: { x: j % w, y: Math.floor(j / w) }, pulse: pulse - 2, sense: 'smelled', look: 0 });
    }
  }
  const fog: FogView = { w, h, veils: veils.map((key) => ({ key, ...VEIL_TEXT[key] })), cells, lit, eyes: view, marks, pulse };
  return { fog, seen };
}

/** `fog=night,weather` → veil keys; `fog=clear` (or `1`) → fog of war with no veil. */
export function parseFogVeils(v: string | null | undefined): FogVeil['key'][] | undefined {
  if (!v) return undefined;
  const keys = v.split(/[,+]/).map((s) => s.trim()).filter((s): s is FogVeil['key'] => s === 'night' || s === 'underground' || s === 'weather');
  return keys;
}
